import type { ScoreEntity } from '@parcel/filters';
import type { ColumnDef } from '@tanstack/react-table';
import type { RecordScores, Score, ScoreTemplateKey } from '../../lib/scores.js';
import { DataTableColumnHeader } from '../data-table/data-table-column-header.js';
import { ScoreBar } from './score-cells.js';

/** Each width fits its header and the meter, so no table leaves a wide gap after the number. */
const COLUMN: Record<
  ScoreEntity,
  { id: string; key: ScoreTemplateKey; title: string; size: number }
> = {
  account: { id: 'icpScore', key: 'icp', title: 'ICP Score', size: 150 },
  project: { id: 'atlasScore', key: 'atlas', title: 'Atlas Score', size: 170 },
  contact: { id: 'prospectScore', key: 'prospect', title: 'Prospect Score', size: 190 },
};

/** The score column every dataset table shares: same header and meter. */
export function scoreColumn<T extends { scores?: RecordScores }>(
  entity: ScoreEntity,
  score: Score | undefined,
): ColumnDef<T> {
  const { id, key, title, size } = COLUMN[entity];
  return {
    id,
    // Unscored records sort last, as the server sorts them.
    accessorFn: (record) => record.scores?.[key]?.score ?? -1,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={title}
        tooltip={`How well the ${entity} matches your scoring criteria, 0-100.`}
      />
    ),
    cell: ({ row }) => (
      <ScoreBar entity={entity} value={row.original.scores?.[key]} score={score} />
    ),
    size,
  };
}
